<template>
  <button v-if="supportEnabled" class="support-fab" type="button" aria-label="打开帮助与反馈" @click="openPanel">
    <span class="support-fab__icon">?</span>
    <span>帮助与反馈</span>
  </button>

  <BaseModal :open="open" size="l" mobile-sheet aria-label="帮助与反馈" @close="closePanel">
    <div class="support-panel">
      <header class="support-head">
        <div>
          <span class="support-eyebrow">HELP & FEEDBACK</span>
          <h2>遇到问题？我们来帮你</h2>
          <p>反馈会直接进入后台处理列表，支付和会员问题可同时联系人工客服。</p>
        </div>
        <button class="icon-button" type="button" aria-label="关闭" @click="closePanel">×</button>
      </header>

      <div v-if="submittedId" class="success-state">
        <span class="success-mark">✓</span>
        <h3>反馈已提交</h3>
        <p>我们已收到你的问题，反馈编号 <strong>#{{ submittedId }}</strong>。</p>
        <div class="success-actions">
          <button class="secondary-button" type="button" @click="resetForm">再提一条</button>
          <button class="primary-button" type="button" @click="closePanel">完成</button>
        </div>
      </div>

      <template v-else>
        <section class="quick-help">
          <button v-for="item in quickHelp" :key="item.type" type="button" :class="{ active: form.type === item.type }" @click="selectType(item.type)">
            <span class="quick-help__icon">{{ item.icon }}</span>
            <span><strong>{{ item.title }}</strong><small>{{ item.hint }}</small></span>
          </button>
        </section>

        <div class="support-body">
          <form v-if="feedbackEnabled" class="feedback-form" @submit.prevent="submitFeedback">
            <label class="field-label">问题类型</label>
            <div class="type-grid">
              <button v-for="item in feedbackTypes" :key="item.value" type="button" :class="{ active: form.type === item.value }" @click="selectType(item.value)">
                {{ item.label }}
              </button>
            </div>

            <label class="field-label" for="feedback-content">请描述遇到的问题 <em>*</em></label>
            <div class="textarea-wrap">
              <textarea id="feedback-content" v-model.trim="form.content" maxlength="1000" rows="5" placeholder="例如：生成的图纸眼睛错位，我使用的是 64×64 模式……" />
              <span>{{ form.content.length }}/1000</span>
            </div>

            <label class="field-label" for="feedback-contact">联系方式 <span>选填</span></label>
            <input id="feedback-contact" v-model.trim="form.contact" maxlength="128" type="text" placeholder="微信号 / 手机号 / 邮箱，方便我们回复你" />

            <p v-if="errorMessage" class="form-error">{{ errorMessage }}</p>
            <button class="primary-button submit-button" type="submit" :disabled="submitting || form.content.length < 5">
              {{ submitting ? "正在提交…" : "提交反馈" }}
            </button>
            <p class="privacy-note">当前页面和登录账号会自动关联，仅用于定位和处理问题。</p>
          </form>

          <aside v-if="showManualSupport" class="manual-support">
            <span class="manual-support__badge">人工客服</span>
            <h3>{{ manualTitle }}</h3>
            <p>{{ supportNotice }}</p>
            <div v-if="qrUrl" class="qr-card"><img :src="qrUrl" alt="拼豆物语客服二维码" /></div>
            <div v-else class="qr-placeholder">客服二维码<br />待后台配置</div>
            <dl>
              <div v-if="wechatId"><dt>客服微信</dt><dd>{{ wechatId }}</dd></div>
              <div><dt>服务时间</dt><dd>{{ serviceHours }}</dd></div>
            </dl>
          </aside>
        </div>
      </template>
    </div>
  </BaseModal>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import { useRoute } from "vue-router";
import BaseModal from "./overlays/BaseModal.vue";
import { createFeedbackApi } from "../api/feedbackApi";
import { useOperationConfig } from "../composables/useOperationConfig";

const route = useRoute();
const api = createFeedbackApi();
const { values, load, isEnabled } = useOperationConfig();
const open = ref(false);
const submitting = ref(false);
const submittedId = ref(null);
const errorMessage = ref("");
const form = reactive({ type: 10, content: "", contact: "" });

const feedbackTypes = [
  { value: 10, label: "图纸效果" },
  { value: 11, label: "颜色识别" },
  { value: 12, label: "下载失败" },
  { value: 20, label: "会员与额度" },
  { value: 21, label: "支付问题" },
  { value: 30, label: "功能建议" },
  { value: 99, label: "其他问题" },
];
const quickHelp = [
  { type: 10, icon: "✦", title: "图纸不理想", hint: "五官、轮廓或颜色问题" },
  { type: 12, icon: "↓", title: "无法下载", hint: "图纸为空或保存失败" },
  { type: 20, icon: "♦", title: "会员与额度", hint: "权益未到账或额度异常" },
  { type: 21, icon: "¥", title: "支付问题", hint: "已付款、订单或退款" },
];

const supportEnabled = computed(() => isEnabled("support.enabled", true));
const feedbackEnabled = computed(() => isEnabled("support.feedback.enabled", true));
const manualEnabled = computed(() => isEnabled("support.manual.enabled", true));
const showManualSupport = computed(() => manualEnabled.value && [20, 21].includes(form.type));
const qrUrl = computed(() => cleanConfig(values.value["support.wechat.qr-url"]));
const wechatId = computed(() => cleanConfig(values.value["support.wechat.id"]));
const serviceHours = computed(() => cleanConfig(values.value["support.service-hours"]) || "工作日 09:00–18:00");
const supportNotice = computed(() => cleanConfig(values.value["support.notice"]) || "支付或会员问题请留下订单号，我们会优先核对。");
const manualTitle = computed(() => form.type === 21 ? "支付问题可优先处理" : "会员权益没到账？");

onMounted(() => {
  load();
  window.addEventListener("bead-open-feedback", openPanel);
});
onBeforeUnmount(() => window.removeEventListener("bead-open-feedback", openPanel));

function cleanConfig(value) {
  const normalized = String(value || "").trim();
  return normalized === "-" || normalized === "未配置" ? "" : normalized;
}
function openPanel() {
  submittedId.value = null;
  errorMessage.value = "";
  open.value = true;
  load(true);
}
function closePanel() { open.value = false; }
function selectType(type) { form.type = type; errorMessage.value = ""; }
function resetForm() {
  Object.assign(form, { type: 10, content: "", contact: "" });
  submittedId.value = null;
  errorMessage.value = "";
}
async function submitFeedback() {
  if (form.content.length < 5) return;
  submitting.value = true;
  errorMessage.value = "";
  try {
    const result = await api.submit({
      ...form,
      sourcePage: route.fullPath,
      appVersion: "web",
    });
    submittedId.value = typeof result === "object" ? result?.id : result;
    if (!submittedId.value) submittedId.value = "已受理";
  } catch (error) {
    errorMessage.value = error?.message || "提交失败，请稍后再试";
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped>
.support-fab{position:fixed;right:24px;bottom:24px;z-index:240;display:flex;align-items:center;gap:8px;padding:11px 16px;border:1px solid rgba(111,92,246,.22);border-radius:999px;background:rgba(255,255,255,.94);color:#4d3ed3;font:800 13px/1 system-ui;box-shadow:0 12px 36px rgba(70,53,150,.18);backdrop-filter:blur(12px);cursor:pointer;transition:.2s ease}.support-fab:hover{transform:translateY(-2px);box-shadow:0 16px 42px rgba(70,53,150,.24)}.support-fab__icon{display:grid;place-items:center;width:22px;height:22px;border-radius:50%;background:linear-gradient(135deg,#745cff,#a37cff);color:#fff;font-size:14px}.support-panel{padding:28px}.support-head{display:flex;align-items:flex-start;justify-content:space-between;gap:20px}.support-eyebrow{color:#765cf6;font-size:11px;font-weight:900;letter-spacing:.12em}.support-head h2{margin:7px 0 6px;color:#262131;font-size:27px}.support-head p{max-width:620px;margin:0;color:#81798e;font-size:14px;line-height:1.65}.icon-button{flex:none;width:36px;height:36px;border:0;border-radius:50%;background:#f4f1f8;color:#746c80;font-size:25px;cursor:pointer}.quick-help{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin:24px 0}.quick-help button{display:flex;align-items:center;gap:9px;min-width:0;padding:12px;border:1px solid #ebe7f1;border-radius:15px;background:#faf9fc;text-align:left;cursor:pointer}.quick-help button.active{border-color:#8069f7;background:#f4f1ff;box-shadow:0 0 0 2px rgba(116,92,255,.08)}.quick-help__icon{display:grid;place-items:center;flex:none;width:30px;height:30px;border-radius:10px;background:#fff;color:#6d55e9;font-weight:900;box-shadow:0 4px 12px rgba(64,48,122,.1)}.quick-help strong,.quick-help small{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.quick-help strong{color:#403948;font-size:13px}.quick-help small{margin-top:4px;color:#9d96a7;font-size:10px}.support-body{display:grid;grid-template-columns:minmax(0,1fr);gap:18px}.support-body:has(.manual-support){grid-template-columns:minmax(0,1fr) 250px}.feedback-form{padding:20px;border:1px solid #ece8f2;border-radius:18px;background:#fff}.field-label{display:block;margin:0 0 8px;color:#443d4e;font-size:13px;font-weight:800}.field-label:not(:first-child){margin-top:17px}.field-label em{color:#f05b78;font-style:normal}.field-label span{color:#a59dac;font-size:11px;font-weight:500}.type-grid{display:flex;flex-wrap:wrap;gap:8px}.type-grid button{padding:8px 11px;border:1px solid #e5e0ec;border-radius:10px;background:#fff;color:#6d6577;font-size:12px;cursor:pointer}.type-grid button.active{border-color:#765cf6;background:#765cf6;color:#fff}.feedback-form textarea,.feedback-form input{box-sizing:border-box;width:100%;border:1px solid #ddd7e6;border-radius:12px;outline:0;background:#fbfafc;color:#342e3c;font:14px/1.6 system-ui;transition:.16s}.feedback-form textarea{resize:vertical;padding:12px 13px 27px}.feedback-form input{height:43px;padding:0 13px}.feedback-form textarea:focus,.feedback-form input:focus{border-color:#8069f7;box-shadow:0 0 0 3px rgba(118,92,246,.1);background:#fff}.textarea-wrap{position:relative}.textarea-wrap>span{position:absolute;right:11px;bottom:8px;color:#aaa3b3;font-size:10px}.form-error{margin:12px 0 0;color:#df4364;font-size:12px}.primary-button,.secondary-button{height:42px;padding:0 18px;border-radius:12px;font-weight:800;cursor:pointer}.primary-button{border:0;background:linear-gradient(135deg,#765cff,#5b55ee);color:#fff;box-shadow:0 9px 22px rgba(91,74,225,.22)}.primary-button:disabled{opacity:.48;cursor:not-allowed}.secondary-button{border:1px solid #ded8e7;background:#fff;color:#625a6d}.submit-button{width:100%;margin-top:17px}.privacy-note{margin:9px 0 0;color:#aaa3b3;font-size:10px;text-align:center}.manual-support{padding:20px;border-radius:18px;background:linear-gradient(160deg,#2c2540,#423568);color:#fff}.manual-support__badge{display:inline-block;padding:5px 8px;border-radius:999px;background:rgba(255,255,255,.12);font-size:10px;font-weight:900;letter-spacing:.08em}.manual-support h3{margin:12px 0 7px;font-size:18px}.manual-support>p{margin:0;color:rgba(255,255,255,.7);font-size:12px;line-height:1.65}.qr-card,.qr-placeholder{display:grid;place-items:center;box-sizing:border-box;width:150px;height:150px;margin:18px auto;padding:8px;border-radius:15px;background:#fff}.qr-card img{width:100%;height:100%;border-radius:9px;object-fit:contain}.qr-placeholder{color:#9b94a4;font-size:12px;line-height:1.7;text-align:center}.manual-support dl{margin:0}.manual-support dl div{display:flex;justify-content:space-between;gap:10px;padding:8px 0;border-top:1px solid rgba(255,255,255,.1)}.manual-support dt{color:rgba(255,255,255,.55);font-size:11px}.manual-support dd{margin:0;font-size:11px;font-weight:800;text-align:right}.success-state{display:grid;place-items:center;padding:54px 20px;text-align:center}.success-mark{display:grid;place-items:center;width:62px;height:62px;border-radius:50%;background:#e7fbef;color:#20a562;font-size:30px;font-weight:900}.success-state h3{margin:17px 0 6px;color:#302a39;font-size:23px}.success-state p{margin:0;color:#81798e}.success-actions{display:flex;gap:10px;margin-top:24px}
@media(max-width:760px){.support-fab{right:14px;bottom:88px;padding:10px}.support-fab>span:last-child{display:none}.support-panel{padding:20px 16px 18px}.support-head h2{font-size:22px}.support-head p{font-size:12px}.quick-help{grid-template-columns:1fr 1fr;margin:18px 0}.support-body:has(.manual-support){grid-template-columns:1fr}.manual-support{order:-1}.manual-support .qr-card,.manual-support .qr-placeholder{width:124px;height:124px}.feedback-form{padding:16px}.type-grid button{flex:1 0 28%}}
</style>
